import {Component, OnInit, OnDestroy, HostListener} from '@angular/core';
import {BusinessEntityService} from '../../../../../services/businessEntity.service';
import BusinessEntity from '../../../../../services/businessEntity.model';
import {FarmSiteService} from '../../../../../services/farmSite.service';
import FarmSiteModel from '../../../../../services/farmSite.model';
import {PaymentTransactionService} from '../../../../../services/paymentTransaction.service';
import PaymentTransactionModel from '../../../../../services/paymentTransaction.model';
import {Sidenavaction} from '../../../../../../assets/js/application/sidenavaction';
import * as moment from 'moment';
import * as _ from 'lodash';
import {ActivatedRoute} from '@angular/router';
import {ExcelService} from 'src/app/services/excelService';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {CommonMethodsService} from 'src/app/services/commonMethods.service';
import {LocationStrategy} from '@angular/common';
import {PreviousRouteService} from 'src/app/services/previousRouteService';
import {BatchLoader} from 'src/app/Utils/batch-loader.util';

declare var $: any;

/**
 * @component PaymentTransactionRegisterComponent
 * @selector app-payment-register-uc
 * @implements {OnInit}
 * @implements {OnDestroy}
 *
 * @description
 * The `PaymentTransactionRegisterComponent` is responsible for managing and displaying the payment transaction register
 * for farm businesses. It serves as a comprehensive reporting tool that allows users to view, filter, and export
 * payment transaction data associated with specific business entities and farm sites.
 *
 * ## Key Responsibilities:
 * 1. **Data Retrieval**: Fetches payment transaction data from the backend based on selected business and farm site criteria.
 * 2. **Filtering & Searching**: Provides robust filtering capabilities including:
 *    - Business selection (for administrative users).
 *    - Farm site selection.
 *    - Date range filtering using a custom date picker.
 *    - Text-based search for Vendor Names and Vendor Sites.
 * 3. **Sorting**: Allows users to sort the transaction list by Farm Site, Vendor Site, or Vendor Name in ascending or descending order.
 * 4. **Pagination/Infinite Scroll**: Implements a batch loading mechanism to handle large datasets efficiently, loading data in chunks as the user scrolls.
 * 5. **Export**: Facilitates the export of the filtered transaction register to an Excel file for offline analysis.
 * 6. **Role-Based Access**: Adapts the interface and data availability based on the logged-in user's role (e.g., 'Business Admin', 'Farmer', 'Farm Manager', 'Employee').
 *
 * ## User Roles & Permissions:
 * - **Farm Category Users**:
 *   - **Business Admin / Farmer**: Automatically linked to their business ID. Can view all farm sites associated with the farm.
 *   - **Farm Manager**: Automatically linked to their business ID. Can view only farm sites assigned to them (specifically in the context of farm spend).
 * - **Other Categories (e.g., Employee)**:
 *   - Must search for and select a business entity to view its payment transactions.
 *   - Can view farm sites associated with the selected business.
 *
 * ## LocalStorage Dependencies:
 * The component relies on the following `localStorage` keys for context and authorization:
 * - `loginPersonType`: Determines the role of the user (e.g., 'Business Admin', 'Farmer').
 * - `loginBusinessCategory`: Determines the category of the business (e.g., 'Farm').
 * - `businessID`: The ID of the logged-in user's business (used for Farm category).
 * - `personID`: The ID of the logged-in user (used for Farm Manager filtering).
 * - `loginBusinessName`: Used for report headers when exporting.
 * - `LoginUserName`: Used for report headers when exporting.
 * - `loginPersonMobileNumber`: Used for report headers when exporting.
 *
 * ## Data Flow:
 * 1. **Initialization (`ngOnInit`)**:
 *    - Checks user role and business category.
 *    - If 'Farm', fetches relevant farm sites.
 *    - If not 'Farm', fetches all available business entities for the dropdown search.
 *    - Initializes the date picker form.
 * 2. **User Interaction**:
 *    - User selects a business (if applicable) and a farm site.
 *    - User clicks "Submit" (or Search button) to trigger `getAllPTRegisterData`.
 * 3. **Data Loading**:
 *    - `getAllPTRegisterData` calls `PaymentTransactionService` to fetch raw data.
 *    - Data is stored in `dupPaymentTransactionRegisters` (master copy).
 *    - `BatchLoader` initializes and loads the first batch into `paymentTransactionRegisters` (display copy).
 * 4. **Filtering**:
 *    - `onFiltersApplied` is triggered by search input or date changes.
 *    - Filters are applied to `dupPaymentTransactionRegisters`.
 *    - Result is updated in `paymentTransactionRegisters`.
 *
 * ## UI State Management:
 * - Uses `responseLoader` for API call loading states.
 * - Uses `listLoader` for infinite scroll loading states.
 * - Manages form validity via `registerForm`.
 * - Handles dropdown visibility for business search via `isDropdownOpen`.
 */
@Component({
  selector: 'app-payment-register-uc',
  templateUrl: './payment-transaction-register.component.html',
  styleUrls: ['./payment-transaction-register.component.scss']
})
export class PaymentTransactionRegisterComponent implements OnInit, OnDestroy {
  /** @type {Array<BusinessEntity>} List of available business entities for selection (used for non-farm users). */
  public businessEntities: Array<BusinessEntity>;

  /** @type {Array<FarmSiteModel>} List of farm sites associated with the selected business. */
  public farmSites: Array<FarmSiteModel>;

  /** @type {Array<PaymentTransactionModel>} The list of payment transactions currently displayed in the UI (subset or filtered). */
  public paymentTransactionRegisters: Array<PaymentTransactionModel>;

  /** @type {PaymentTransactionModel} The model used for binding filter criteria (business_id, farm_site_id). */
  public paymentTransactionRegister: PaymentTransactionModel;

  /** @type {Array<PaymentTransactionModel>} The master list of all fetched payment transactions, used for filtering. */
  public dupPaymentTransactionRegisters: Array<PaymentTransactionModel>;

  /** @type {string} The type of the logged-in person (e.g., 'Business Admin', 'Farmer'). Retrieved from localStorage. */
  public loginPersonType: string;

  /** @type {string} The name of the logged-in user. */
  public LoginUserName: string;

  /** @type {string} The name of the logged-in user's business. */
  public loginUserBusinessName: string;

  /** @type {string} The category of the logged-in user's business (e.g., 'Farm'). */
  public loginUserBusinessCategory: string;

  /** @type {boolean} Flag indicating if the search button has been clicked. */
  public isSearchClicked: boolean;

  /** @type {boolean} Flag controlling the main loading spinner for API requests. */
  public responseLoader: boolean;

  /** @type {boolean} Flag for sorting by Farm Site (ascending/descending). */
  public siteAsc: boolean;

  /** @type {boolean} Flag for sorting by Vendor Name (ascending/descending). */
  public nameAsc: boolean;

  /** @type {boolean} Flag for sorting by Vendor Site (ascending/descending). */
  public vsiteAsc: boolean;

  /** @type {any} Application permissions object. */
  public appPermission: any;

  /** @type {string} Keyword for search operations. */
  public keyword: string;

  /** @type {any} Generic item holder. */
  public item: any;

  /** @type {string} The current search string entered by the user. */
  public search: string;

  /** @type {boolean} Flag to display a "No Data" message. Default is `false`. */
  public NoDataMessage: boolean = false;

  /** @type {number} The current active page number for pagination. */
  public activePageNo: number;

  /** @type {boolean} Flag to trigger clearing of the search input. */
  public clearSearchInput: boolean;

  /** @type {FormGroup} The main reactive form for business and location selection. */
  public registerForm: FormGroup;

  /** @type {boolean} Flag controlling the visibility of the business selection dropdown. Default is `false`. */
  public isDropdownOpen = false;

  /** @type {any[]} Filtered list of options for the business autocomplete dropdown. Default is `[]`. */
  public filteredOptions: any[] = [];

  /** @type {string} Placeholder or value for the business search input. Default is 'Enter the business name'. */
  public businessSearch: string = 'Enter the business name';

  /** @type {boolean} Flag indicating if the form has been submitted (for validation display). */
  public submitted: boolean;

  /** @type {moment.Moment} The maximum allowed date for the date picker (current date). */
  maxDate = moment();

  /** @type {{ startDate: moment.Moment, endDate: moment.Moment }} The currently selected date range. */
  selected: { startDate: moment.Moment, endDate: moment.Moment };

  /** @type {boolean} Flag to disable the submit button during processing. */
  public submitButtonDisabled: boolean;

  /** @type {string} The type of filter currently active. */
  public filterType: string;

  /** @type {boolean} Flag indicating if any filters have been applied. */
  public filtersApplied: boolean;

  /** @type {boolean} Flag indicating if the submit action has been triggered successfully. */
  public isSubmitClicked: boolean;

  /** @type {boolean} Flag indicating if a date filter is currently applied. */
  public dateFilterApplied: boolean;

  /** @type {boolean} Flag controlling the spinner for list batch loading. Default is `false`. */
  public listLoader = false;              // Show listLoader spinner

  /** @type {number} The starting index for batch loading. Default is `0`. */
  public startIndex = 0;             // Tracks how much has been loaded

  /** @type {boolean} Flag indicating if client-side filtering is currently active. Default is `false`. */
  public isFiltering: boolean = false;

  /** @type {boolean} Flag indicating if the list is empty. Default is `false`. */
  public noList: boolean = false;

  /** @type {BatchLoader<any>} Utility for handling batch loading of data. */
  private batchLoader: BatchLoader<any>;

  /** @type {FormGroup} Reactive form for the date range picker. */
  dateRangeForm!: FormGroup;

  /**
   * Constructor for PaymentTransactionRegisterComponent.
   * Initializes the component and injects necessary services.
   *
   * @param {FormBuilder} formBuilder - Service for building reactive forms.
   * @param {ExcelService} excelService - Service for exporting data to Excel.
   * @param {ActivatedRoute} route - Service for retrieving route information.
   * @param {BusinessEntityService} businessEntityService - Service for business entity operations.
   * @param {FarmSiteService} farmSitesService - Service for farm site operations.
   * @param {PaymentTransactionService} paymentTransactionRegisterService - Service for payment transaction operations.
   * @param {Sidenavaction} sidenavaction - Service for side navigation actions.
   * @param {CommonMethodsService} commonMethodsService - Service for common utility methods.
   * @param {LocationStrategy} locationStrategy - Service for interacting with the browser's URL.
   * @param {PreviousRouteService} previousRouteService - Service for tracking route history.
   *
   * @service FormBuilder
   * @service ExcelService
   * @service ActivatedRoute
   * @service BusinessEntityService
   * @service FarmSiteService
   * @service PaymentTransactionService
   * @service Sidenavaction
   * @service CommonMethodsService
   * @service LocationStrategy
   * @service PreviousRouteService
   */
  constructor(public formBuilder: FormBuilder, public excelService: ExcelService, public route: ActivatedRoute, public businessEntityService: BusinessEntityService, public farmSitesService: FarmSiteService, public paymentTransactionRegisterService: PaymentTransactionService, public sidenavaction: Sidenavaction, public commonMethodsService: CommonMethodsService,
              private locationStrategy: LocationStrategy, private previousRouteService: PreviousRouteService
  ) {
    this.paymentTransactionRegister = new PaymentTransactionModel();
    // Initialize the main register form with validation
    this.registerForm = this.formBuilder.group({
      businessfield: ['', Validators.required],
      locationField: ['', Validators.required],
      dateRangeField: [null],
    });
  }

  /**
   * @lifecycle ngOnInit
   * @async
   * @description
   * Lifecycle hook that is called after data-bound properties of a directive are initialized.
   * - Retrieves user context from `localStorage`.
   * - Initializes the date picker.
   * - Sets up the previous route.
   * - Determines the logic flow based on `loginBusinessCategory`:
   *   - If 'Farm': Auto-sets business ID and fetches farm sites based on `loginPersonType`.
   *   - If other: Fetches all business entities for selection.
   *
   * @throws {Error} Captures and displays errors via `commonMethodsService` if initialization fails.
   */
  public async ngOnInit() {
    try {
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.initializedatePicker();
      this.search = '';
      this.filterType = '';
      this.filtersApplied = false;
      this.isSubmitClicked = false;
      window.scrollTo(0, 0);
      this.isFiltering = false;
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      switch (localStorage.getItem('loginBusinessCategory')) {
        case 'Farm':
          this.paymentTransactionRegister.business_id = localStorage.businessID;
          this.registerForm.get('businessfield').setValidators(null);
          this.registerForm.get('businessfield').updateValueAndValidity();
          switch (this.loginPersonType) {
            case 'Business Admin':
            case 'Farmer':
              this.farmSites = await this.farmSitesService.getFarmeSitesByFarmsId(localStorage.businessID);
              break;
            default:
              this.farmSites = await this.farmSitesService.getFarmeSitesByFarmManagerIdInFarmSpend(localStorage.businessID, localStorage.personID);
              break;
          }
          if (this.farmSites.length === 1) {
            this.paymentTransactionRegister.farm_site_id = this.farmSites[0].id; // model-binding key
          }
          break;
        default:
          this.businessSearch = undefined;
          this.registerForm.get('businessfield').patchValue('');
          let businesses = await this.businessEntityService.getAllBusiness();
          this.businessEntities = businesses.filter(obj => {
            return ((obj.businessType && obj.businessType.length > 0 && obj.businessType[0].businessTypeID && obj.businessType[0].businessTypeID != '618a18ac0f339c001e991b08') && (obj.businessType && obj.businessType.length > 0 && obj.businessType[0].businessTypeID && obj.businessType[0].businessTypeID != '62b3fa8998be51001d17a310'));
          });
          this.filteredOptions = this.businessEntities;
          break;
      }
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @method f
   * @description Getter for easy access to form controls in the HTML template.
   * @returns {any} The controls of the `registerForm`.
   */
  public get f() {
    return this.registerForm.controls;
  }

  /**
   * @method filterBusinesses
   * @description Filters the `businessEntities` list based on the user's input in the business field.
   * Updates `filteredOptions` with the result.
   * @side-effect Updates `filteredOptions`.
   */
  public filterBusinesses(): void {
    const value = (this.registerForm.get('businessfield')!.value || '') as BusinessEntity | string;
    const searchText =
      typeof value === 'string' ? value.toLowerCase() : value.businessName.toLowerCase();
    this.filteredOptions = this.businessEntities.filter(o =>
      o.businessName.toLowerCase().includes(searchText)
    );
  }

  /**
   * @method selectOption
   * @async
   * @description Handles the selection of a business entity from the dropdown.
   * - Updates the form value and model.
   * - Fetches farm sites associated with the selected business.
   * - Auto-selects the farm site if only one exists.
   * @param {BusinessEntity} business - The selected business entity.
   */
  public async selectOption(business: BusinessEntity) {
    this.registerForm.get('businessfield')!.setValue(business.businessName);
    this.isDropdownOpen = false;
    this.businessSearch = business.businessName;
    this.paymentTransactionRegister.business_id = business.id;
    if (this.paymentTransactionRegister.business_id) {
      this.paymentTransactionRegister.farm_site_id = undefined;
      this.farmSites = await this.farmSitesService.getFarmeSitesByFarmId(this.paymentTransactionRegister.business_id);
      if (this.farmSites.length == 1) {
        this.paymentTransactionRegister.farm_site_id = this.farmSites[0].id; // model-binding key
      }
    }
  }

  /**
   * @method onFiltersApplied
   * @description Applies filters to the payment transaction list.
   * This is a central method that orchestrates filtering based on:
   * - Text search (`search` property).
   * - Date range (`dateFilterApplied` property).
   * If no filters are active, it resets the list to the initial batch loaded state.
   * @side-effect Updates `paymentTransactionRegisters`, `isFiltering`, `batchLoader`.
   */
  public onFiltersApplied() {
    this.paymentTransactionRegisters = [];
    this.isFiltering = true;
    const value = this.search && this.search.replace(/[-_ :.]/g, '').toLowerCase();
    if (this.search != '') {
      if (this.dateFilterApplied) {
        this.applySearchWithFilterType(value);
      } else {
        this.applySearchFilter(value);
      }
    } else {
      if (this.dateFilterApplied) {
        this.applyFilterWithFilterType();
      } else {
        this.isFiltering = false;
        this.initializeBatchLoader();
        this.batchLoader.reset();
        this.batchLoader.loadNextBatch(true);
      }
    }
  }

  /**
   * @method applySearchFilter
   * @private
   * @description Filters the transaction list based on a search string. Checks 'Vendor.name' and 'Vendor.site'.
   * @param {string} searchValue - The normalized search string.
   */
  private applySearchFilter(searchValue: string) {
    this.paymentTransactionRegisters = this.dupPaymentTransactionRegisters.filter((paymentTransactionsObj: any) =>
      ['Vendor.name', 'Vendor.site'].some(property => {
        const nestedPropertyValue = property.split('.').reduce((acc, current) => acc && acc[current], paymentTransactionsObj);
        return nestedPropertyValue && nestedPropertyValue.replace(/[-_ :.]/g, '').toLowerCase().includes(searchValue);
      })
    );
  }

  /**
   * @method applyFilterWithFilterType
   * @private
   * @description Filters the transaction list based on the selected date range.
   * If the date range is invalid, it falls back to `onFiltersApplied` (effectively clearing date filter).
   */
  private applyFilterWithFilterType() {
    if (this.selected && this.selected.startDate && this.selected.endDate) {
      const startDate = this.selected.startDate.startOf('day');
      const endDate = this.selected.endDate.endOf('day');
      this.paymentTransactionRegisters = this.dupPaymentTransactionRegisters.filter((obj: any) => {
        const recordDate = moment(obj.date); // direct parse
        return recordDate.isSameOrAfter(startDate) && recordDate.isSameOrBefore(endDate);
      });
    } else {
      this.dateFilterApplied = false;
      this.onFiltersApplied();
    }
  }

  /**
   * @method applySearchWithFilterType
   * @private
   * @description Applies both date range filtering and text search filtering simultaneously.
   * @param {string} searchValue - The normalized search string.
   */
  private applySearchWithFilterType(searchValue: string) {
    if (this.selected && this.selected.startDate && this.selected.endDate) {
      const startDate = this.selected.startDate.startOf('day');
      const endDate = this.selected.endDate.endOf('day');

      this.paymentTransactionRegisters = this.dupPaymentTransactionRegisters.filter((obj: any) => {
        const recordDate = moment(obj.date);
        const isInDateRange = recordDate.isSameOrAfter(startDate) && recordDate.isSameOrBefore(endDate);

        const matchesSearch = ['Vendor.name', 'Vendor.site'].some(property => {
          const nestedValue = property.split('.').reduce((acc, curr) => acc && acc[curr], obj);
          return nestedValue && nestedValue.replace(/[-_ :.]/g, '').toLowerCase().includes(searchValue);
        });

        return isInDateRange && matchesSearch;
      });
    } else {
      this.dateFilterApplied = false;
      this.applySearchFilter(searchValue);
    }
  }

  /**
   * @method clearFilters
   * @description Clears specific filters based on the input type.
   * @param {string} filterFrom - The source of the clear action ('search' or other for date).
   */
  public clearFilters(filterFrom: string) {
    switch (filterFrom) {
      case 'search':
        this.search = '';
        this.onFiltersApplied();
        break;
      default:
        this.dateFilterApplied = false;
        this.selected = undefined;
        this.onFiltersApplied();
        break;
    }
  }

  /**
   * @method getAllPTRegisterData
   * @async
   * @description Fetches the payment transaction register data from the server.
   * - Validates the form.
   * - Resets filters and search states.
   * - Calls the service to get data.
   * - Initializes the batch loader for pagination.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  public async getAllPTRegisterData() {
    this.submitted = true;
    this.registerForm.markAllAsTouched();
    if (this.registerForm.invalid) return
    this.submitted = false;
    this.isSubmitClicked = false;
    this.dateFilterApplied = false;
    this.selected = undefined;
    this.search = '';
    this.isFiltering = false;
    this.paymentTransactionRegisters = [];
    this.dupPaymentTransactionRegisters = [];
    try {
      this.submitButtonDisabled = true;
      this.responseLoader = true;
      this.dupPaymentTransactionRegisters = await this.paymentTransactionRegisterService.paymentTransactionRegisterData(this.paymentTransactionRegister.business_id, this.paymentTransactionRegister.farm_site_id);
      this.initializeBatchLoader(); // Reset instance
      this.batchLoader.reset();     // Clear state
      await this.batchLoader.loadNextBatch(true); // Load first batch
      this.registerForm.markAsPristine(); // optional - clears dirty flags
      this.registerForm.markAsUntouched();
      this.clearDateRange();
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    } finally {
      this.responseLoader = false;
      this.submitButtonDisabled = false;
      this.isSubmitClicked = true;
    }
  }

  /**
   * @method onScroll
   * @description Host listener for window scroll events. Triggers `checkScrollAndLoad` to handle infinite scrolling.
   * @lifecycle HostListener
   */
  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private scrollTimeout: any;

  /**
   * @method checkScrollAndLoad
   * @private
   * @description Debounced handler for scroll events. Checks if the user has scrolled near the bottom of the page
   * and triggers the loading of the next batch of data if not currently filtering or loading.
   */
  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 &&
        !this.batchLoader?.isLoading() && !this.isFiltering) {
        this.batchLoader?.loadNextBatch();
      }
    }, 50);
  }

  /**
   * @method initializeBatchLoader
   * @private
   * @description Initializes the `BatchLoader` utility with the master data list and callback functions for UI updates.
   */
  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.dupPaymentTransactionRegisters,
      (updatedList) => {
        this.paymentTransactionRegisters = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined, // <-- omit or pass undefined
      (loading) => this.listLoader = loading
    );
  }

  /**
   * @method initializedatePicker
   * @description Initializes the `dateRangeForm` and subscribes to value changes to manage `dateFilterApplied` state.
   * @side-effect Creates `dateRangeForm`.
   */
  public initializedatePicker() {
    this.dateRangeForm = this.formBuilder.group({
      start: [null],
      end: [null]
    });
    this.dateRangeForm.valueChanges.subscribe(val => {
      const {start, end} = val;
      if (!start || !end) {
        this.dateFilterApplied = false;
      }
    });
  }

  /**
   * @method onPickerClosed
   * @description Handles the closing event of the date picker.
   * Ensures a valid range is selected (defaults end date to start date if missing) and triggers filtering.
   * @side-effect Updates `dateRangeForm` value, `dateFilterApplied`, and calls `onDateChanged`.
   */
  public onPickerClosed(): void {
    const {start, end} = this.dateRangeForm.value;
    if (start && !end) {
      // Only start selected → default end = start
      this.dateRangeForm.patchValue({end: start}, {emitEvent: false});
      this.dateFilterApplied = true;
      this.onDateChanged({startDate: start, endDate: start});
    } else if (start && end) {
      this.dateFilterApplied = true;
      this.onDateChanged({startDate: start, endDate: end});
    } else {
      this.dateFilterApplied = false;
    }
  }

  /**
   * @method onDateChanged
   * @description Updates the `selected` date range property and triggers filter application.
   * @param {{ startDate: Date; endDate: Date }} range - The selected date range.
   * @side-effect Updates `selected` and calls `onFiltersApplied`.
   */
  public onDateChanged(range: { startDate: Date; endDate: Date }): void {
    this.selected = {
      startDate: moment(range.startDate),
      endDate: moment(range.endDate)
    };
    this.onFiltersApplied(); // reuse your logic
  }

  /**
   * @method clearDateRange
   * @description Resets the date range form and clears the date filter.
   * @side-effect Resets `dateRangeForm`, `dateFilterApplied`, `selected`, and calls `onFiltersApplied`.
   */
  public clearDateRange(): void {
    this.dateRangeForm.reset();
    this.dateFilterApplied = false;
    this.selected = undefined;
    this.onFiltersApplied();
  }

  /**
   * @method sortingPTRegister
   * @description Sorts the displayed payment transaction list based on the specified type.
   * Supports sorting by 'site' (Farm Site), 'vsite' (Vendor Site), and 'vname' (Vendor Name).
   * @param {string} type - The field to sort by ('site', 'vsite', 'vname').
   */
  public sortingPTRegister(type: string) {
    if (type == 'site') {
      this.siteAsc = !this.siteAsc;
      let orderbyval = 'desc';
      if (this.siteAsc == true) {
        orderbyval = 'asc';
      }
      this.paymentTransactionRegisters = _.orderBy(this.paymentTransactionRegisters, ['farmSite[0].farmNameAtLocation'], [orderbyval]);
      this.nameAsc = false;
      this.vsiteAsc = false;
    }
    if (type == 'vsite') {
      this.vsiteAsc = !this.vsiteAsc;
      let orderbyval = 'desc';
      if (this.vsiteAsc == true) {
        orderbyval = 'asc';
      }
      this.paymentTransactionRegisters = _.orderBy(this.paymentTransactionRegisters, ['Vendor.site'], [orderbyval]);
      this.nameAsc = false;
      this.siteAsc = false;
    }
    if (type == 'vname') {
      this.nameAsc = !this.nameAsc;
      let orderbyval = 'desc';
      if (this.nameAsc == true) {
        orderbyval = 'asc';
      }
      this.paymentTransactionRegisters = _.orderBy(this.paymentTransactionRegisters, ['Vendor.name'], [orderbyval]);
      this.vsiteAsc = false;
      this.siteAsc = false;
    }
  }

  /**
   * @method openFullScreen
   * @description Toggles the fullscreen mode for the element with ID 'zoomView'.
   * @returns {void}
   * This method is used to Opens the specified element in fullscreen mode if the browser supports it.
   */
  public openFullScreen(): void {
    let element = document.getElementById('zoomView') as HTMLElement;
    if (element.requestFullscreen) {
      element.requestFullscreen();
    }
  }

  /**
   * @method exportExcel
   * @async
   * @description Generates and downloads an Excel report of the payment transaction register.
   * Constructs the request body with current filter context and user details.
   * @throws {Error} Captures and displays errors via `commonMethodsService`.
   */
  public async exportExcel() {
    try {
      const reqBody = {
        "business_id": this.paymentTransactionRegister.business_id,
        "businessName": this.loginPersonType == 'EMPLOYEE' ? this.businessSearch : localStorage.getItem('loginBusinessName'),
        "farm_site_id": this.paymentTransactionRegister.farm_site_id,
        "farmNameAtLocation": this.farmSites.find((farmSiteObj: FarmSiteModel) => farmSiteObj.id == this.paymentTransactionRegister.farm_site_id).farmNameAtLocation,
        "updatedAt": "new",
        "personName": localStorage.getItem('LoginUserName'),
        "mobileNumber": localStorage.getItem('loginPersonMobileNumber')
      }
      const fileObj = this.paymentTransactionRegisterService.excelReportsDownload('vendorPaymentRegister', reqBody);
      fileObj.subscribe(fileblob => {
        if (fileblob) {
          this.excelService.saveAsExcelFile(fileblob, 'Vendor Payment Register Report');
        }
      })
    } catch (error) {
      this.commonMethodsService.userFriendlyAlertMessages(error);
    }
  }

  /**
   * @lifecycle ngOnDestroy
   * @description Lifecycle hook that is called when the component is destroyed.
   * Cancels ongoing HTTP requests and cleans up the batch loader to prevent memory leaks.
   * @side-effect Cancels HTTP requests, destroys `batchLoader`.
   */
  public ngOnDestroy(): void {
    this.farmSitesService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.paymentTransactionRegisterService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }
}
